import merge from 'lodash/merge';

export interface CookiePathConfig {
    name: string;
    equalityCheck?: (a: any, b: any) => boolean;
    deleteCheck?: (value: any) => boolean;
}

export type CookiePaths = Record<string, CookiePathConfig>;

export interface ReduxCookiesMiddlewareOptions {
    setCookie: (name: string, value: string, expiry?: number) => void;
    logger?: (message: string) => void;
    defaultEqualityCheck?: (a: any, b: any) => boolean;
    defaultDeleteCheck?: (value: any) => boolean;
}

const safeGetCookie = (getCookie: (name: string) => any, cookieName: string): any => {
    try {
        return JSON.parse(getCookie(cookieName));
    } catch (_) {
        return getCookie(cookieName);
    }
};

const getStateFromPath = (path: string, valueFromCookie: any): any => {
    const pathSplit = path.split('.');
    return pathSplit.reverse()
        .reduce((finalPathState, currPathKey) => ({ [currPathKey]: finalPathState }), valueFromCookie);
};

export const getStateFromCookies = (
    preloadedState: any = {},
    paths: CookiePaths,
    getCookie: (name: string) => any
): any => {
    const pathStates = Object.keys(paths).map((path) => {
        const pathConfig = paths[path];
        const valueFromCookie = safeGetCookie(getCookie, pathConfig.name);
        return getStateFromPath(path, valueFromCookie);
    });

    return pathStates.reduce((finalState, pathState) => merge({}, finalState, pathState), preloadedState);
};

const reduxCookiesMiddleware = (paths: CookiePaths = {}, customOptions: ReduxCookiesMiddlewareOptions) => {
    const options = {
        // eslint-disable-next-line no-console
        logger: console.error,
        defaultEqualityCheck: (a: any, b: any) => (a === b),
        defaultDeleteCheck: (value: any) => (typeof value === 'undefined'),
        ...customOptions
    };

    const getVal = (state: any, path: string): any => {
        const pathPartsList = path.split('.');
        let value = state;
        let index;

        for (index = 0; index < pathPartsList.length; index += 1) {
            const pathPart = pathPartsList[index];

            if (Object.hasOwnProperty.call(value, pathPart)) {
                value = value[pathPart];
            } else {
                options.logger(`state not found at store.getState().${path}`);
                break;
            }
        }

        return (index === pathPartsList.length) ? value : null;
    };

    return (store: any) => (next: any) => (action: any) => {
        const prevState = store.getState();
        const result = next(action);
        const nextState = store.getState();

        Object.keys(paths).forEach((pathToState) => {
            const prevVal = getVal(prevState, pathToState);
            const nextVal = getVal(nextState, pathToState);
            const state = paths[pathToState];
            const equalityCheck = state.equalityCheck || options.defaultEqualityCheck;
            const deleteCheck = state.deleteCheck || options.defaultDeleteCheck;

            if (!equalityCheck(prevVal, nextVal)) {
                if (deleteCheck(nextVal)) {
                    options.setCookie(state.name, JSON.stringify(nextVal), 0);
                } else {
                    options.setCookie(state.name, JSON.stringify(nextVal));
                }
            }
        });

        return result;
    };
};

export default reduxCookiesMiddleware;
